/**
 * PostOpsPage.tsx
 *
 * The Post-ops worklist, ported from legacy postops_full_list-test.php.
 *
 * Shell copied from pages/qa/QaPage.tsx, which is the closest sibling: a
 * filtered worklist with stat tiles and pill tabs above a server-paged table.
 *
 * The pill tabs carry no counts, unlike QaPage's. Legacy's post-op tabs have
 * none either (plain links, postops_full_list-test.php:339-354), and each count
 * would re-run the nine-table worklist join; the stat tiles already carry the
 * numbers staff act on.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PatientLink } from '@/components/ui/PatientLink';
import { cn } from '@/lib/utils';
import { buildStaffOptions, staffQueryKey } from '@/lib/staffOptions';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useDebounce } from '@/hooks/useDebounce';
import { usePageContext } from '@/hooks/usePageContext';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import { isDateRange, isSelect } from '@/types/filters.types';
import { useLocalDashboardFilters, useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import * as adminSvc from '@/services/admin.service';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import * as postOpsSvc from '@/services/post-ops.service';
import type { PostOpsListItem, PostOpsListParams, PostOpsReasonType } from '@/services/post-ops.service';
import {
  POST_OPS_SORT_KEYS,
  POST_OPS_TABS,
  POST_OPS_TILES,
  POST_OPS_ROW_STATUS_STYLES,
  POST_OPS_QA_STATUS_OPTIONS,
  POST_OPS_SEARCH_TYPES,
  POST_OPS_NEGATABLE_FILTERS,
  DEFAULT_POST_OPS_SORT,
  DEFAULT_POST_OPS_SORT_DIR,
  DEFAULT_POST_OPS_TAB,
  formatPostOpsDate,
} from './postOpsShared';
import type { PostOpsSortKey, PostOpsSearchType } from './postOpsShared';

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search post-op cases...' },
  { key: 'procdaterange', label: 'Procedure Date', type: 'daterange' },
  { key: 'doctor', label: 'Doctor', type: 'select', allLabel: 'All doctors', multi: true },
  { key: 'qastatus', label: 'Q/A', type: 'select', allLabel: 'All Q/A', multi: true },
  { key: 'reason', label: 'Reason', type: 'select', allLabel: 'All reasons', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
];

const PAGE_SIZE = 25;

function selectedValues(value: FilterValue | null | undefined): string[] {
  if (!isSelect(value)) return [];
  return (Array.isArray(value) ? value : [value]).map(String).filter((v) => v !== '');
}

export default function PostOpsPage() {
  const navigate = useNavigate();
  usePageContext('Post-ops');

  const filterState = useLocalDashboardFilters(FILTER_CONFIGS, 'post-ops');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);

  const [activeTab, setActiveTab] = useLocalStorage<number>('ftp-post-ops-tab', DEFAULT_POST_OPS_TAB);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<PostOpsSortKey>(DEFAULT_POST_OPS_SORT);
  const [order, setOrder] = useState<'asc' | 'desc'>(DEFAULT_POST_OPS_SORT_DIR);
  const [searchType, setSearchType] = useState<PostOpsSearchType>('name');

  const rawSearch = isSelect(effectiveValues.search) ? String(effectiveValues.search) : '';
  const search = useDebounce(rawSearch, 300);

  // ── Lookup data for the sidebar ─────────────────────────────────────────
  // Doctors is a FILTER, so it includes people who have left -- narrowing it to
  // active staff would make a departed physician's cases unfindable -- and the
  // shared builder labels them as inactive, as QaPage's does.
  const { data: doctors = [] } = useQuery({
    queryKey: staffQueryKey('doctors', [], 'all'),
    queryFn: () => listDoctors({ includeInactive: true }),
  });
  const { data: facilities = [] } = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: adminSvc.listFacilities,
    staleTime: 5 * 60_000,
  });
  const { data: reasons = [] } = useQuery<PostOpsReasonType[]>({
    queryKey: ['post-ops-reason-types'],
    queryFn: postOpsSvc.listPostOpsReasonTypes,
    staleTime: 5 * 60_000,
  });

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      doctor: buildStaffOptions(
        doctors,
        (d) => d.doctorID,
        (d) => formatDoctorName(d) || `#${d.doctorID}`,
      ).map((item) => ({ value: String(item.value), label: item.label })),
      facility: facilities
        .filter((f) => f.active)
        .map((f) => ({ value: String(f.facilityId), label: f.facilityName || 'Unknown' })),
      reason: reasons.map((r: PostOpsReasonType) => ({ value: String(r.id), label: r.title })),
      qastatus: POST_OPS_QA_STATUS_OPTIONS,
    }),
    [doctors, facilities, reasons],
  );

  const params = useMemo<PostOpsListParams>(() => {
    const procRange = effectiveValues.procdaterange;

    // Only negations on filters that actually carry a value; a negated empty
    // filter is a no-op, and the server drops it anyway.
    const filterPlus = POST_OPS_NEGATABLE_FILTERS.filter(
      (key) => filterState.negations[key] === true && selectedValues(effectiveValues[key]).length > 0,
    );

    return {
      tab: activeTab,
      page,
      perPage: PAGE_SIZE,
      sortBy: sort,
      sortDir: order,
      search: search || undefined,
      searchType: search ? searchType : undefined,
      doctorIds: selectedValues(effectiveValues.doctor).map(Number),
      facilityIds: selectedValues(effectiveValues.facility).map(Number),
      reasonIds: selectedValues(effectiveValues.reason).map(Number),
      qaStatusIds: selectedValues(effectiveValues.qastatus),
      procDateFrom: isDateRange(procRange) ? procRange.from || undefined : undefined,
      procDateTo: isDateRange(procRange) ? procRange.to || undefined : undefined,
      filterPlus: filterPlus.length > 0 ? [...filterPlus] : undefined,
    };
  }, [activeTab, page, sort, order, search, searchType, effectiveValues, filterState.negations]);

  // Any change other than the page itself returns to page 1, or a filter can
  // leave you stranded on a page that no longer exists. An effect, not a memo:
  // React does not promise a memo runs only when its deps change, and a dropped
  // cache would snap the user back to page 1 mid-browse.
  const filterSignature = JSON.stringify({ ...params, page: 0 });
  useEffect(() => {
    setPage(1);
  }, [filterSignature]);

  const { data, isLoading, isPlaceholderData, isError, error } = useQuery({
    queryKey: ['post-ops-list', params],
    queryFn: () => postOpsSvc.listPostOps(params),
    placeholderData: (prev) => prev,
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const tiles = data?.meta?.tiles;

  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'post-ops',
    entityLabel: 'post-op cases',
  });

  // The server's refusal messages are written for the person reading them --
  // what is wrong and who can fix it -- so show them rather than a generic line.
  const refusal = isError
    ? (error as { userMessage?: string } | null)?.userMessage ??
      'Post-op cases could not be loaded. Refresh the page, or contact support if it keeps happening.'
    : null;

  const columns = useMemo<DataTableColumn<PostOpsListItem>[]>(
    () => [
      {
        key: 'procedureDate',
        header: 'Proc. Date',
        sortable: true,
        render: (row) => formatPostOpsDate(row.procedureDate),
      },
      {
        key: 'patientName',
        header: 'Patient',
        sortable: true,
        role: 'title',
        render: (row) =>
          row.patientName ? (
            <PatientLink
              patientId={row.patientId}
              patientName={row.patientName}
              className="font-medium text-blue-600 hover:underline"
              // Without this the link fires AND the row's own navigation fires.
              onClick={(e) => e.stopPropagation()}
            >
              {row.patientName}
            </PatientLink>
          ) : (
            <span className="text-slate-500">{'—'}</span>
          ),
      },
      {
        key: 'procedures',
        header: 'Procedure(s)',
        sortable: true,
        truncate: true,
        render: (row) => row.procedures || '—',
      },
      {
        key: 'lastVisit',
        header: 'Last Visit',
        sortable: true,
        render: (row) => formatPostOpsDate(row.lastVisit),
      },
      {
        key: 'nextVisit',
        header: 'Next Visit',
        sortable: true,
        render: (row) => formatPostOpsDate(row.nextVisit),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        // Blank for a plan nobody has opened yet -- legacy shows nothing there,
        // and an invented label would read as a real status.
        render: (row) => row.status || '—',
      },
      {
        key: 'qa',
        header: 'Q/A',
        sortable: true,
        // Legacy draws a red icon for an open case and a green one for a
        // resolved case; the state is the information, so keep it.
        render: (row) =>
          row.isQa ? (
            <Badge variant={row.qaResolved ? 'success' : 'warning'} size="sm">
              {row.qaResolved ? 'Resolved' : 'Open'}
            </Badge>
          ) : (
            '—'
          ),
      },
      {
        key: 'reason',
        header: 'Reason',
        sortable: true,
        truncate: true,
        render: (row) => row.reasons || '—',
      },
      {
        key: 'surveyResult',
        header: 'Survey Result',
        sortable: true,
        align: 'right',
        render: (row) => row.surveyResult || '—',
      },
      {
        key: 'photoConsent',
        header: 'Photo Consent',
        sortable: true,
        render: (row) => row.photoConsent || '—',
      },
    ],
    [],
  );

  function handleSortChange(key: string, direction: 'asc' | 'desc') {
    if (!(POST_OPS_SORT_KEYS as readonly string[]).includes(key)) return;
    setSort(key as PostOpsSortKey);
    setOrder(direction);
    setPage(1);
  }

  return (
    <ListPageLayout
      filterSidebar={
        <FilterSidebar
          filters={FILTER_CONFIGS}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="post-ops"
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
        />
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 space-y-5 p-6">
          <PageHeader
            variant="compact"
            title="Post-ops"
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">Search by</span>
                  <select
                    className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    value={searchType}
                    aria-label="Search by"
                    onChange={(e) => {
                      setSearchType(e.target.value as PostOpsSearchType);
                      setPage(1);
                    }}
                  >
                    {POST_OPS_SEARCH_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  variant="secondary"
                  onClick={() => startExport(params as Record<string, unknown>)}
                  disabled={total === 0 || exporting || isError}
                  loading={exporting}
                >
                  <Download className="h-4 w-4 mr-1" /> Export
                </Button>
                {exportModal}
              </div>
            }
          />

          {refusal ? (
            <p className="text-sm text-slate-500">{refusal}</p>
          ) : (
            <>
              <MetricCardGrid columns={6}>
                {POST_OPS_TILES.map((tile) => (
                  <StatCard
                    key={tile.key}
                    label={tile.label}
                    value={tiles ? tiles[tile.key] : 0}
                    tone={tile.tone}
                  />
                ))}
              </MetricCardGrid>

              <div className="flex gap-2 flex-wrap">
                {POST_OPS_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab.id);
                      setPage(1);
                    }}
                    className={cn(
                      'px-3.5 py-1.5 text-sm font-medium rounded-full border transition-colors',
                      activeTab === tab.id
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {!refusal && (
          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<PostOpsListItem>
              data={rows}
              columns={columns}
              keyField="planId"
              loading={isLoading || isPlaceholderData}
              manualSort
              sortKey={sort}
              sortDir={order}
              onSortChange={handleSortChange}
              rowClassName={(row) => POST_OPS_ROW_STATUS_STYLES[row.rowStatus]}
              onRowClick={(row) => navigate(`/patients/${row.patientId}/plans/${row.planId}?section=post-ops`)}
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              emptyTitle="No post-op cases"
              emptyDescription="Try adjusting your search or filters."
            />
          </div>
        )}
      </div>
    </ListPageLayout>
  );
}
